<template>
  <div class="landing">
    <BlogNav />

    <!-- HERO -->
    <section class="hero">
      <div class="hero-inner">
        <h1 class="hero-title">
          Ce que Query Juriste fait<br />
          <span class="font-bold">et ne fait pas</span>
        </h1>
        <p class="hero-sub">
          Un outil d'aide au raisonnement juridique,
          <strong>pas un avocat</strong>
        </p>
      </div>
    </section>

    <!-- INTRO -->
    <section class="content-section">
      <div class="content-inner">
        <p class="intro-text">
          L'essor des outils d'intelligence artificielle dans le domaine juridique suscite autant d'enthousiasme que de confusion.
          Entre assistants conversationnels, bases de données juridiques et plateformes de consultation, il devient parfois difficile de comprendre le rôle exact de chaque outil.
        </p>
        <p class="intro-text">
          Query Juriste a fait un choix clair : <strong>ne pas se positionner comme un avocat</strong>, ni comme un service de consultation juridique, mais comme un <strong>outil d'aide au raisonnement juridique</strong>.
        </p>
        <p class="intro-text">
          Voici précisément ce que Query Juriste fait - et ce qu'il ne fait pas.
        </p>
      </div>
    </section>

    <!-- CE QUE QUERY JURISTE FAIT -->
    <section class="feature-section">
      <div class="feature-inner">
        <h2 class="section-title">Ce que Query Juriste fait</h2>

        <div class="feature-card">
          <div class="feature-number">1</div>
          <div class="feature-content">
            <h3 class="feature-heading">Structurer le raisonnement juridique face à une décision à risque</h3>
            <p class="feature-text">
              Certaines situations juridiques ne se résument pas à une réponse "oui / non".
              Elles impliquent des choix, des arbitrages et des zones d'incertitude.
            </p>
            <p class="feature-text">
              Pour ce type de décision, Query Juriste aide à structurer le raisonnement juridique et à identifier les principaux risques, sans se substituer à un conseil juridique personnalisé.
            </p>
            <p class="feature-highlight">
              L'objectif n'est pas de décider à votre place, mais de mettre de l'ordre dans une situation complexe.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">2</div>
          <div class="feature-content">
            <h3 class="feature-heading">Tester et challenger un raisonnement existant</h3>
            <p class="feature-text">
              De nombreux utilisateurs arrivent avec une intuition, ou une première analyse.
            </p>
            <p class="feature-text">
              Si vous souhaitez tester ou vérifier la solidité de votre raisonnement juridique, sans demander un avis juridique formel, Query Juriste est conçu pour ça.
            </p>
            <p class="feature-highlight">
              L'outil permet de confronter une logique, d'identifier ses failles potentielles et de vérifier sa cohérence.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">3</div>
          <div class="feature-content">
            <h3 class="feature-heading">Analyser des situations très contextuelles</h3>
            <p class="feature-text">
              Le droit dépend presque toujours du contexte : faits, timing, acteurs, secteur, juridiction.
            </p>
            <p class="feature-text">
              Lorsque la question dépend fortement du contexte, Query Juriste aide à analyser la situation de manière juridique et structurée, et à faire ressortir les points juridiques à vérifier.
            </p>
            <p class="feature-highlight">
              Plutôt que de fournir une réponse générique, l'outil met en lumière ce qui compte réellement dans votre situation.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">4</div>
          <div class="feature-content">
            <h3 class="feature-heading">Clarifier les enjeux avant ou en complément d'un avocat</h3>
            <p class="feature-text">
              Consulter un avocat est souvent indispensable, mais encore faut-il savoir quoi lui demander.
            </p>
            <p class="feature-text">
              En amont ou en complément d'un échange avec un avocat, Query Juriste permet de clarifier les enjeux juridiques, les options possibles et les zones d'incertitude.
            </p>
            <p class="feature-highlight">
              Cela permet d'aborder l'échange avec un professionnel du droit de manière plus structurée et efficace.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">5</div>
          <div class="feature-content">
            <h3 class="feature-heading">Aller vite sans sacrifier la rigueur</h3>
            <p class="feature-text">
              Dans la pratique, le temps manque souvent.
            </p>
            <p class="feature-text">
              Pour aller vite tout en gardant une approche juridique rigoureuse, Query Juriste sert de copilote de raisonnement et aide à réduire le risque d'erreur d'analyse du raisonnement.
            </p>
            <p class="feature-highlight">
              Il s'agit d'un outil d'aide à la réflexion, pensé pour les situations réelles, sous contrainte de temps.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">6</div>
          <div class="feature-content">
            <h3 class="feature-heading">Apporter de la sécurité intellectuelle</h3>
            <p class="feature-text">
              Beaucoup d'utilisateurs ne cherchent pas une réponse définitive, mais une forme de réassurance intellectuelle.
            </p>
            <p class="feature-text">
              Si votre objectif est d'être plus sûr de votre raisonnement, plutôt que d'obtenir une réponse définitive, Query Juriste est adapté.
            </p>
            <p class="feature-highlight">
              L'outil aide à prendre du recul et à mieux comprendre les implications juridiques d'une décision.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">7</div>
          <div class="feature-content">
            <h3 class="feature-heading">Être utilisé par les professionnels du droit</h3>
            <p class="feature-text">
              Query Juriste est également conçu pour un usage professionnel.
            </p>
            <p class="feature-text">
              Les professionnels du droit l'utilisent comme un outil de structuration et de challenge de l'analyse juridique, et non comme un substitut à leur propre avis ou responsabilité.
            </p>
            <p class="feature-highlight">
              Il s'intègre comme un outil de travail, pas comme une autorité.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">8</div>
          <div class="feature-content">
            <h3 class="feature-heading">Cartographier les risques et scénarios</h3>
            <p class="feature-text">
              Le droit est rarement binaire.
            </p>
            <p class="feature-text">
              Query Juriste est pertinent pour identifier les risques juridiques, les alternatives possibles et les zones d'incertitude d'une situation, en fonction des scénarios envisagés.
            </p>
            <p class="feature-highlight">
              Cette approche par scénarios permet de mieux anticiper les conséquences possibles d'un choix.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CE QUE QUERY JURISTE NE FAIT PAS -->
    <section class="not-section">
      <div class="not-inner">
        <h2 class="section-title section-title-light">Ce que Query Juriste ne fait pas</h2>
        <p class="not-intro">
          Pour éviter toute ambiguïté, il est tout aussi important de préciser ce que Query Juriste ne fait pas.
        </p>

        <ul class="not-list">
          <li class="not-item">
            <span class="not-icon">❌</span>
            <span>Query Juriste ne fournit pas d'avis juridique</span>
          </li>
          <li class="not-item">
            <span class="not-icon">❌</span>
            <span>Query Juriste ne remplace pas un avocat ou un juriste</span>
          </li>
          <li class="not-item">
            <span class="not-icon">❌</span>
            <span>Query Juriste ne délivre pas de conseil juridique personnalisé</span>
          </li>
          <li class="not-item">
            <span class="not-icon">❌</span>
            <span>Query Juriste ne garantit pas la conformité juridique d'une décision</span>
          </li>
        </ul>

        <p class="not-conclusion">
          Query Juriste n'apporte pas de réponses juridiques toutes faites, et ne se substitue jamais à un conseil juridique engageant.
        </p>
      </div>
    </section>

    <!-- RÉSUMÉ -->
    <section class="summary-section">
      <div class="summary-inner">
        <h2 class="section-title">En résumé</h2>
        <p class="summary-main">
          Query Juriste est un <strong>outil d'aide au raisonnement juridique</strong> pour des situations concrètes.
        </p>
        <p class="summary-text">Il s'adresse à celles et ceux qui doivent :</p>

        <ul class="summary-list">
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            Comprendre les enjeux juridiques d'une situation réelle
          </li>
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            Structurer leur analyse
          </li>
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            Identifier les risques et les zones d'incertitude
          </li>
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            Sans attendre une décision ou un avis juridique formel
          </li>
        </ul>

        <p class="summary-conclusion">
          Dans un environnement juridique de plus en plus complexe, Query Juriste se positionne volontairement comme un <strong>outil d'analyse et de structuration</strong>, au service de décisions plus éclairées, et non comme un substitut au conseil juridique.
        </p>
      </div>
    </section>

    <!-- CTA BAND -->
    <section class="cta-band">
      <div class="cta-inner">
        <div class="cta-copy">
          <h3 class="cta-title">Une démo personnalisée, sans engagement</h3>
          <p class="cta-text">
            Découvrez comment notre outil révolutionne le travail des avocats et juristes.
          </p>
        </div>

        <div class="cta-form">
          <a
            class="btn btn-ghost rounded-pill"
            href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
            @click.prevent="openCalendlyDemo"
          >
            Réserver une démo
          </a>
          <p class="cta-foot">Tarifs sur mesure, adaptés à votre structure</p>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-logo-section">
          <img src="/logo-query-juriste-png.png" alt="Query Juriste" class="logo" />
          <p class="footer-description">
            L'IA juridique qui raisonne avec vos besoins.
            Simplifiez vos recherches juridiques.
          </p>
        </div>

        <div class="footer-links-grid">
          <div class="footer-column">
            <h4>Pages</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/">Accueil</a></li>
              <li><a href="https://www.query-juriste.com/blog">Blog</a></li>
              <li><a href="https://www.query-juriste.com/ia">L'IA Query Juriste</a></li>
              <li><a href="https://www.query-juriste.com/sources">Sources & couverture</a></li>
              <li><a href="https://www.query-juriste.com/faq">FAQ</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Produit</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/loginPage">Connexion</a></li>
              <li><a href="https://www.query-juriste.com/contactPage">Contact</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Entreprise</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/mentionsPage">Mentions légales</a></li>
              <li><a href="https://www.query-juriste.com/conditions-utilisation">CGU</a></li>
              <li><a href="https://www.query-juriste.com/conditions-vente">CGV</a></li>
              <li><a href="https://www.query-juriste.com/politique-donnees">Confidentialité</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>© 2025 Query Juriste, tous droits réservés</p>
      </div>
    </footer>
  </div>
</template>

<script>
import BlogNav from "@/components/BlogNav.vue";
import { openCalendlyDemo } from "@/utils/calendly";

export default {
  name: "IAPage",
  components: { BlogNav },
  methods: {
    openCalendlyDemo,
  },
  metaInfo() {
    return {
      title: "Ce que Query Juriste fait - et ne fait pas",
      meta: [
        {
          name: "description",
          content:
            "Découvrez ce que Query Juriste fait et ne fait pas : un outil d'aide au raisonnement juridique, pas un avocat ni un service de consultation juridique.",
        },
      ],
      link: [{ rel: "canonical", href: "https://www.query-juriste.com/ia" }],
    };
  },
};
</script>

<style scoped>
/* ---------- Tokens ---------- */
:root {
  --bg: #ffffff;
  --text: #1a1a1a;
  --muted: #5e6470;
  --primary: #7e5cf2;
  --primary-600: #625CFB;
  --info: #625CFB;
  --surface: #f7f8fa;
  --border: #e6e8ec;
  --shadow: 0 10px 30px rgba(0,0,0,.12);
  --shadow-sm: 0 4px 16px rgba(0,0,0,.08);
  --radius: 14px;
}

/* ---------- Utilities ---------- */
.text-primary { color: #625CFB; }
.text-info { color: #625CFB; }
.font-bold { font-weight: 700; color: #625CFB; }
.rounded-xl { border-radius: 20px; }
.rounded-pill { border-radius: 999px; }
.shadow-lg { box-shadow: var(--shadow); }
.shadow-sm { box-shadow: var(--shadow-sm); }

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 22px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform .06s ease, background .2s ease, color .2s ease, border-color .2s ease;
  will-change: transform;
}
.btn:active { transform: translateY(1px); }

.btn-ghost {
  background: transparent;
  border-color: #fff;
  color: #fff;
}
.btn-ghost:hover {
  background: rgba(255,255,255,.08);
  border-color: #fff;
}

/* ---------- Layout wrappers ---------- */
.landing {
  font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
  background: var(--bg);
}

/* ---------- HERO ---------- */
.hero {
  padding: 110px 20px 40px;
  background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
}
.hero-inner {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}
.hero-title {
  font-size: clamp(32px, 5vw, 52px);
  line-height: 1.15;
  margin-bottom: 0.8em;
}
.hero-sub {
  margin: 10px 0 24px;
  color: #5e6470;
  font-size: clamp(16px, 2.6vw, 20px);
}

/* ---------- CONTENT SECTION ---------- */
.content-section {
  padding: 50px 20px;
}
.content-inner {
  max-width: 800px;
  margin: 0 auto;
}
.intro-text {
  font-size: 18px;
  line-height: 1.7;
  color: #1a1a1a;
  margin-bottom: 20px;
}
.intro-text strong {
  color: #625CFB;
}

/* ---------- FEATURE SECTION ---------- */
.feature-section {
  padding: 60px 20px;
  background: #f8f9fc;
}
.feature-inner {
  max-width: 900px;
  margin: 0 auto;
}
.section-title {
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
}
.feature-card {
  display: flex;
  gap: 24px;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.feature-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0,0,0,.1);
}
.feature-number {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #625CFB;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}
.feature-content {
  flex: 1;
}
.feature-heading {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 16px;
  color: #1a1a1a;
}
.feature-text {
  font-size: 16px;
  line-height: 1.7;
  color: #5e6470;
  margin: 0 0 12px;
}
.feature-highlight {
  font-size: 16px;
  line-height: 1.7;
  color: #1a1a1a;
  font-weight: 500;
  margin: 0;
  padding-left: 16px;
  border-left: 3px solid #625CFB;
}

/* ---------- NOT SECTION ---------- */
.not-section {
  padding: 70px 20px;
  background: #1a1a1a;
  color: #fff;
}
.not-inner {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}
.section-title-light {
  color: #fff;
}
.not-intro {
  font-size: 18px;
  line-height: 1.7;
  color: rgba(255,255,255,.8);
  margin-bottom: 40px;
}
.not-list {
  list-style: none;
  padding: 0;
  margin: 0 0 40px;
  text-align: left;
}
.not-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: rgba(255,255,255,.05);
  border-radius: 12px;
  margin-bottom: 12px;
  font-size: 17px;
  font-weight: 500;
}
.not-icon {
  font-size: 20px;
}
.not-conclusion {
  font-size: 18px;
  line-height: 1.7;
  color: rgba(255,255,255,.9);
  font-style: italic;
}

/* ---------- SUMMARY SECTION ---------- */
.summary-section {
  padding: 70px 20px;
}
.summary-inner {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}
.summary-main {
  font-size: 22px;
  line-height: 1.6;
  color: #1a1a1a;
  margin-bottom: 24px;
}
.summary-main strong {
  color: #625CFB;
}
.summary-text {
  font-size: 18px;
  color: #5e6470;
  margin-bottom: 24px;
}
.summary-list {
  list-style: none;
  padding: 0;
  margin: 0 auto 32px;
  max-width: 500px;
  text-align: left;
}
.summary-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 17px;
  font-weight: 500;
  color: #1a1a1a;
}
.summary-list .check {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}
.summary-conclusion {
  font-size: 18px;
  line-height: 1.7;
  color: #5e6470;
  max-width: 700px;
  margin: 0 auto;
}
.summary-conclusion strong {
  color: #625CFB;
}

/* ---------- CTA BAND ---------- */
.cta-band {
  background: #625CFB;
  color: #fff;
  margin: 0;
  padding: 50px 20px;
}
.cta-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: center;
}
.cta-title {
  font-weight: 500;
  font-size: clamp(22px, 3.6vw, 32px);
  margin: 0 0 8px;
}
.cta-text { margin: 0; opacity: .95; }
.cta-foot { margin-top: 10px; opacity: .95; }

/* ---------- FOOTER ---------- */
.footer {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 3rem 0 0 0;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  display: flex;
  justify-content: space-between;
  gap: 4rem;
  align-items: start;
}

.footer-logo-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-logo-section .logo {
  height: 60px;
  width: 60px;
  align-items: center;
  margin-bottom: 0.5rem;
}

.footer-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;
}

.footer-links-grid {
  display: flex;
  gap: 3rem;
}

.footer-column h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-column li {
  margin: 0;
}

.footer-column a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
  line-height: 1.4;
}

.footer-column a:hover {
  color: #625CFB;
}

.footer-bottom {
  margin-top: 2rem;
  padding: 1.5rem 0;
  border-top: 1px solid #e5e7eb;
  background-color: #f8fafc;
}

.footer-bottom p {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

/* ---------- Responsive ---------- */
@media (max-width: 1100px) {
  .cta-inner {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .feature-card {
    flex-direction: column;
    padding: 24px;
  }
  .feature-number {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
}

@media (max-width: 640px) {
  .hero { padding-top: 80px; }
}

/* Footer responsive */
@media (max-width: 1024px) {
  .footer-links-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .footer-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 1rem;
    text-align: center;
  }

  .footer-logo-section {
    align-items: center;
  }

  .footer-description {
    text-align: center;
    max-width: none;
  }

  .footer-links-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  .footer-column {
    text-align: left;
  }

  .footer-bottom p {
    padding: 0 1rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .footer-links-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .footer-column {
    text-align: center;
  }
}
</style>
